import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Tree from 'react-d3-tree';
import { useMobileOptimization } from '../hooks/useMobileOptimization';
import { parseGedcomToTree, convertToD3Tree, findRootPersons, searchPersons, getPersonDetails, getSearchSuggestions, clearAllFilters } from '../utils/gedcomParser';
import { downloadGedcomFile, saveToLocalStorage, loadFromLocalStorage, hasStoredChanges, clearStoredData } from '../utils/gedcomExporter';
import RelationshipFinder from '../components/RelationshipFinder';
import TableView from '../components/TableView';
import LoadingProgressIndicator from '../components/LoadingProgressIndicator';
import ExportShareControls from '../components/ExportShareControls';
import { usePerformanceManager } from '../hooks/usePerformanceManager';
import { useOfflineData } from '../components/OfflineSupport';
import '../components/FamilyTree.css';

const DESKTOP_BREAKPOINT = 1024;

const FamilyTreePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile, isTablet, isTouchDevice } = useMobileOptimization();
  
  // State management
  const [treeData, setTreeData] = useState(null);
  const [gedcomData, setGedcomData] = useState(null);
  const [rootPersons, setRootPersons] = useState([]);
  const [selectedRoot, setSelectedRoot] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showNoResultsHelp, setShowNoResultsHelp] = useState(false);
  const [error, setError] = useState(null);
  const treeContainer = useRef(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(0.8);
  
  // Enhanced features state
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showRelationshipFinder, setShowRelationshipFinder] = useState(false);
  const [filters, setFilters] = useState({
    generation: 'all',
    gender: 'all',
    location: 'all',
    dateRange: 'all'
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveOptions, setShowSaveOptions] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  
  // Table view state
  const [viewMode, setViewMode] = useState('table'); // 'tree' or 'table'

  // Full-screen desktop layout state
  const [isDesktopFullscreen, setIsDesktopFullscreen] = useState(false);
  const fullscreenContainerRef = useRef(null);

  // Performance management
  const {
    startLoading,
    updateProgress,
    endLoading,
    isLoading,
    trackError,
    clearError,
    cacheData,
    getCachedData,
    queueForSync
  } = usePerformanceManager('family-tree');

  // Offline data management for GEDCOM file
  const {
    data: offlineGedcomData,
    loading: offlineLoading,
    error: offlineError,
    isFromCache,
    refresh: refreshOfflineData
  } = useOfflineData(
    'gedcom-data',
    async () => {
      const response = await fetch('/data/rossetti.ged');
      if (!response.ok) {
        throw new Error(`Failed to load GEDCOM file: ${response.status}`);
      }
      return await response.text();
    },
    { ttl: 24 * 60 * 60 * 1000 } // Cache for 24 hours
  );

  // Full-screen desktop layout: apply class only after confirming viewport meets desktop threshold
  // and container dimensions are computed (sequenced per Req 5.5)
  useEffect(() => {
    const checkDesktopLayout = () => {
      const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
      if (isDesktop && fullscreenContainerRef.current) {
        // Confirm container dimensions are computed before applying fullscreen class
        const rect = fullscreenContainerRef.current.getBoundingClientRect();
        if (rect.width > 0) {
          setIsDesktopFullscreen(true);
        }
      } else {
        setIsDesktopFullscreen(false);
      }
    };

    // Initial check
    checkDesktopLayout();

    // Listen for resize events
    window.addEventListener('resize', checkDesktopLayout);
    return () => window.removeEventListener('resize', checkDesktopLayout);
  }, [treeData, gedcomData]);

  // URL state management
  useEffect(() => {
    // Read URL parameters and set initial state
    const rootParam = searchParams.get('root');
    const zoomParam = searchParams.get('zoom');
    const viewParam = searchParams.get('view');
    const searchParam = searchParams.get('search');
    const selectedParam = searchParams.get('selected');

    if (rootParam && rootParam !== selectedRoot) {
      setSelectedRoot(rootParam);
    }
    if (zoomParam) {
      const zoomValue = parseFloat(zoomParam);
      if (!isNaN(zoomValue) && zoomValue >= 0.1 && zoomValue <= 3) {
        setZoom(zoomValue);
      }
    }
    if (viewParam && ['tree', 'table'].includes(viewParam)) {
      setViewMode(viewParam);
    }
    if (searchParam) {
      setSearchTerm(searchParam);
    }
    if (selectedParam && gedcomData) {
      const personDetails = getPersonDetails(gedcomData, selectedParam);
      if (personDetails) {
        setSelectedPerson(personDetails);
      }
    }
  }, [searchParams, selectedRoot, gedcomData]);

  // Update URL when state changes
  const updateUrlState = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadGedcomFile = useCallback(async () => {
    try {
      clearError();
      const loadingState = startLoading();
      updateProgress(10, 'Checking for stored changes...');
      
      // Check for stored changes first
      const storedData = loadFromLocalStorage();
      let parsed;
      let gedcomContent;
      updateProgress(20, 'Loading family tree data...');
      
      if (storedData && storedData.gedcomContent) {
        // Use stored data if available
        updateProgress(40, 'Loading stored changes...');
        console.log('Loading stored changes from localStorage');
        gedcomContent = storedData.gedcomContent;
        parsed = parseGedcomToTree(storedData.gedcomContent);
        setHasUnsavedChanges(true);
      } else {
        // Use offline data if available, otherwise fetch
        if (offlineGedcomData) {
          updateProgress(40, 'Loading cached data...');
          gedcomContent = offlineGedcomData;
          parsed = parseGedcomToTree(offlineGedcomData);
        } else {
          updateProgress(30, 'Fetching family tree data...');
          // Load the original GEDCOM file from public/data folder
          const response = await fetch('/data/rossetti.ged');
          if (!response.ok) {
            throw new Error('Failed to load family tree data');
          }
          
          gedcomContent = await response.text();
          updateProgress(50, 'Parsing family tree data...');
          parsed = parseGedcomToTree(gedcomContent);
          
          // Cache the data for offline use
          cacheData('gedcom-content', gedcomContent, 24 * 60 * 60 * 1000);
        }
        setHasUnsavedChanges(false);
      }
      
      updateProgress(70, 'Processing family data...');
      setGedcomData(parsed);
      
      // Extract unique locations for filter dropdown
      updateProgress(80, 'Extracting locations...');
      const locations = extractUniqueLocations(parsed);
      setAvailableLocations(locations);
      
      updateProgress(90, 'Finding root persons...');
      const roots = findRootPersons(parsed);
      setRootPersons(roots);
      
      if (roots.length > 0) {
        updateProgress(95, 'Building family tree...');
        // Try to find Pasquale Rossetti as the default root, otherwise use first root
        const pasqualeRoot = roots.find(person => 
          person.name.toLowerCase().includes('pasquale') && 
          person.name.toLowerCase().includes('rossetti')
        );
        const defaultRoot = pasqualeRoot || roots[0];
        
        // Use URL parameter if available, otherwise use default
        const rootFromUrl = searchParams.get('root');
        const rootToUse = rootFromUrl && roots.find(r => r.id === rootFromUrl) ? rootFromUrl : defaultRoot.id;
        
        setSelectedRoot(rootToUse);
        const tree = convertToD3Tree(parsed, rootToUse);
        setTreeData(tree);
        
        // Update URL with initial state
        updateUrlState({ root: rootToUse, view: viewMode });
      }
      
      updateProgress(100, 'Family tree loaded successfully!');
      endLoading(true);
    } catch (err) {
      console.error('Error loading GEDCOM file:', err);
      const errorMessage = 'Failed to load Rossetti family tree data';
      setError(errorMessage);
      trackError(err, { 
        context: 'loadGedcomFile',
        hasStoredData: !!offlineGedcomData,
        hasOfflineData: !!offlineGedcomData,
        isFromCache
      });
      endLoading(false, err);
    }
  }, [offlineGedcomData, startLoading, updateProgress, endLoading, trackError, clearError, cacheData, searchParams, viewMode, updateUrlState]);

  // Load GEDCOM file on component mount
  useEffect(() => {
    loadGedcomFile();
  }, [loadGedcomFile]);

  // Center the tree when container is ready
  useEffect(() => {
    if (treeContainer.current) {
      const dimensions = treeContainer.current.getBoundingClientRect();
      setTranslate({
        x: dimensions.width / 2,
        y: 100
      });
    }
  }, [treeData]);

  const handleRootChange = (personId) => {
    if (gedcomData && personId) {
      setSelectedRoot(personId);
      const tree = convertToD3Tree(gedcomData, personId);
      setTreeData(tree);
      updateUrlState({ root: personId });
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setShowNoResultsHelp(false);
    updateUrlState({ search: term });
    
    if (gedcomData && term && term.trim().length >= 2) {
      const results = searchPersons(gedcomData, term);
      setSearchResults(results);
      
      // If no results found, get suggestions
      if (results.length === 0) {
        const suggestions = getSearchSuggestions(gedcomData, term);
        setSearchSuggestions(suggestions);
        setShowNoResultsHelp(true);
      } else {
        setSearchSuggestions([]);
      }
    } else {
      setSearchResults([]);
      setSearchSuggestions([]);
    }
  };

  const handleSearchSelect = (personId) => {
    handleRootChange(personId);
    setSearchTerm('');
    setSearchResults([]);
    setSearchSuggestions([]);
    setShowNoResultsHelp(false);
    updateUrlState({ search: '', root: personId });
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    const clearedFilters = clearAllFilters(filters);
    setFilters(clearedFilters);
    setSearchTerm('');
    setSearchResults([]);
    setSearchSuggestions([]);
    setShowNoResultsHelp(false);
    updateUrlState({ search: '' });
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    handleSearch(suggestion);
  };

  // Enhanced node click handler
  const handleNodeClick = (nodeData) => {
    if (gedcomData && nodeData.attributes?.id) {
      const personDetails = getPersonDetails(gedcomData, nodeData.attributes.id);
      setSelectedPerson(personDetails);
      updateUrlState({ selected: personDetails.id });
      navigate(`/person/${personDetails.id}`, {
        state: { person: personDetails, gedcomData, returnTo: '/family-tree' }
      });
    }
  };

  // Handle person selection from search or other components
  const handlePersonSelect = (person) => {
    navigate(`/person/${person.id}`, {
      state: { person, gedcomData, returnTo: '/family-tree' }
    });
  };

  // Handle table view person selection
  const handleTablePersonSelect = (personId) => {
    if (gedcomData && personId) {
      const personDetails = getPersonDetails(gedcomData, personId);
      navigate(`/person/${personDetails.id}`, {
        state: { person: personDetails, gedcomData, returnTo: '/family-tree' }
      });
    }
  };

  // Handle switch from table to tree view
  const handleSwitchToTree = (personId) => {
    if (personId) {
      handleRootChange(personId);
    }
    setViewMode('tree');
    updateUrlState({ view: 'tree' });
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    updateUrlState({ view: mode });
  };

  // Handle zoom change
  const handleZoomChange = (newZoom) => {
    setZoom(newZoom);
    updateUrlState({ zoom: newZoom.toFixed(2) });
  };

  // Handle saving edited person data
  const handleSaveEdit = async (personId, updatedPerson) => {
    try {
      // Update the person in the gedcomData
      if (gedcomData && gedcomData.individuals[personId]) {
        Object.assign(gedcomData.individuals[personId], updatedPerson);
        
        // Update the selected person state
        setSelectedPerson(updatedPerson);
        
        // Regenerate the tree to reflect changes
        if (selectedRoot) {
          const tree = convertToD3Tree(gedcomData, selectedRoot);
          setTreeData(tree);
        }
        
        // Update available locations in case new places were added
        const locations = extractUniqueLocations(gedcomData);
        setAvailableLocations(locations);
        
        // Save to localStorage for persistence
        const saveSuccess = saveToLocalStorage(gedcomData);
        if (saveSuccess) {
          setHasUnsavedChanges(true);
          console.log('Person data updated and saved to localStorage:', updatedPerson);
        } else {
          console.warn('Failed to save to localStorage, changes are only in memory');
        }
        
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error saving person data:', error);
      throw error;
    }
  };

  // Handle downloading GEDCOM file
  const handleDownloadGedcom = () => {
    if (gedcomData) {
      const success = downloadGedcomFile(gedcomData, 'rossetti_updated.ged');
      if (success) {
        setHasUnsavedChanges(false);
        alert('GEDCOM file downloaded successfully! Your changes have been saved.');
      } else {
        alert('Error downloading GEDCOM file. Please try again.');
      }
    }
  };

  // Handle clearing stored changes
  const handleClearChanges = () => {
    if (window.confirm('Are you sure you want to discard all changes and reload the original data?')) {
      clearStoredData();
      setHasUnsavedChanges(false);
      loadGedcomFile(); // Reload original data
    }
  };

  // Extract unique locations from GEDCOM data
  const extractUniqueLocations = (gedcomData) => {
    if (!gedcomData || !gedcomData.individuals) return [];
    
    const locations = new Set();
    
    Object.values(gedcomData.individuals).forEach(person => {
      // Add birth places
      if (person.birthPlace && person.birthPlace.trim()) {
        // Extract country/main location (last part after comma)
        const birthLocation = person.birthPlace.split(',').pop().trim();
        if (birthLocation) {
          locations.add(birthLocation);
        }
      }
      
      // Add death places
      if (person.deathPlace && person.deathPlace.trim()) {
        // Extract country/main location (last part after comma)
        const deathLocation = person.deathPlace.split(',').pop().trim();
        if (deathLocation) {
          locations.add(deathLocation);
        }
      }
    });
    
    // Convert to sorted array
    return Array.from(locations).sort();
  };

  // Custom node rendering with enhanced interactivity
  const renderCustomNode = ({ nodeDatum, toggleNode }) => (
    <g>
      <circle
        r={22}
        fill={nodeDatum.attributes?.sex === 'M' ? '#4A90E2' : '#E24A90'}
        stroke={selectedPerson?.id === nodeDatum.attributes?.id ? '#FFD700' : '#333'}
        strokeWidth={selectedPerson?.id === nodeDatum.attributes?.id ? '3' : '2'}
        onClick={(e) => {
          e.stopPropagation();
          handleNodeClick(nodeDatum);
        }}
        onDoubleClick={toggleNode}
        style={{ cursor: 'pointer' }}
      />
      <text
        fill="white"
        strokeWidth="0"
        x="0"
        y="5"
        textAnchor="middle"
        fontSize="12"
        fontWeight="bold"
        style={{ pointerEvents: 'none' }}
      >
        {nodeDatum.name.split(' ')[0]}
      </text>
      <text
        fill="#333"
        strokeWidth="0"
        x="0"
        y="40"
        textAnchor="middle"
        fontSize="10"
        style={{ maxWidth: '120px', pointerEvents: 'none' }}
      >
        {nodeDatum.name}
      </text>
      {nodeDatum.attributes?.birth && (
        <text
          fill="#666"
          strokeWidth="0"
          x="0"
          y="55"
          textAnchor="middle"
          fontSize="8"
          style={{ pointerEvents: 'none' }}
        >
          b. {nodeDatum.attributes.birth}
        </text>
      )}
      {nodeDatum.attributes?.death && (
        <text
          fill="#666"
          strokeWidth="0"
          x="0"
          y="68"
          textAnchor="middle"
          fontSize="8"
          style={{ pointerEvents: 'none' }}
        >
          d. {nodeDatum.attributes.death}
        </text>
      )}
      {/* Enhanced visual indicators */}
      {nodeDatum.children && nodeDatum.children.length > 0 && (
        <circle
          cx="18"
          cy="-18"
          r="6"
          fill="#28a745"
          stroke="white"
          strokeWidth="1"
          style={{ pointerEvents: 'none' }}
        />
      )}
    </g>
  );

  // Page actions for the header
  const pageActions = [
    {
      label: hasUnsavedChanges ? 'Save Changes' : 'Export',
      onClick: () => setShowSaveOptions(!showSaveOptions),
      icon: 'fas fa-save',
      variant: hasUnsavedChanges ? 'primary' : 'secondary'
    },
    {
      label: 'Find Relationship',
      onClick: () => setShowRelationshipFinder(true),
      icon: 'fas fa-route',
      variant: 'secondary'
    }
  ];

  // Breadcrumb path
  const breadcrumbPath = [
    { label: 'Rossetti', route: '/rossetti', icon: 'fas fa-users' },
    { label: 'Family Tree', route: '/family-tree', icon: 'fas fa-sitemap' }
  ];

  if (isLoading || offlineLoading) {
    return (
      <div className="page-container-wrapper max-width-default padding-default">
        <div className="page-header">
          <h1>
            <i className="fas fa-sitemap"></i>
            Rossetti Family Tree
          </h1>
          <p>Loading family tree data...</p>
        </div>
        
        <div className="page-content">
          <LoadingProgressIndicator
            componentId="family-tree"
            showProgress={true}
            showMessage={true}
            showDataSize={false}
          />
          {isFromCache && (
            <div className="cache-notice">
              <i className="fas fa-info-circle"></i>
              Using cached data - some information may be outdated
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container-wrapper max-width-default padding-default">
        <div className="page-header">
          <h1>
            <i className="fas fa-sitemap"></i>
            Rossetti Family Tree
          </h1>
          <p>Error loading family tree</p>
        </div>
        
        <div className="page-content">
          <div className="error-message">
            <h3>Error Loading Family Tree</h3>
            <p>{error}</p>
            <button onClick={() => navigate('/')} className="cta-btn primary">
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={fullscreenContainerRef}
      className={`page-container-wrapper max-width-full padding-none ${isDesktopFullscreen ? 'family-tree-fullscreen' : ''} ${isMobile() ? 'mobile-optimized' : ''} ${isTouchDevice() ? 'touch-optimized' : ''}`}
    >
      {/* Page Header */}
      <div className="page-header">
        <h1>
          <i className="fas fa-sitemap"></i>
          Rossetti Family Tree
        </h1>
        <p>{hasUnsavedChanges ? "Unsaved Changes" : "Interactive family tree browser"}</p>
        
        {/* Action Buttons */}
        <div className="page-header-actions">
          {pageActions.map((action, index) => (
            <button 
              key={index}
              className={`page-action-btn ${action.variant || 'primary'}`}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              <i className={action.icon}></i>
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {/* Page Content */}
      <div className="page-content">
      {/* Save Options Panel */}
      {showSaveOptions && (
        <div className="save-options-panel">
          <h3><i className="fas fa-save"></i> Save & Export Options</h3>
          
          {/* Enhanced Export Controls */}
          <div className="export-controls-section">
            <h4>Export & Share</h4>
            <ExportShareControls 
              data={gedcomData}
              contentType="gedcom"
              title="Family Tree"
              showBackupControls={true}
              className="family-tree-export-controls"
            />
          </div>
          
          <div className="save-options-grid">
            <button 
              className="save-option download-option"
              onClick={handleDownloadGedcom}
            >
              <i className="fas fa-download"></i>
              <div className="option-info">
                <strong>Download GEDCOM File</strong>
                <span>Save changes to a .ged file on your computer</span>
              </div>
            </button>
            
            <button 
              className="save-option storage-option"
              onClick={() => {
                if (gedcomData) {
                  saveToLocalStorage(gedcomData);
                  alert('Changes saved to browser storage!');
                }
              }}
            >
              <i className="fas fa-hdd"></i>
              <div className="option-info">
                <strong>Save to Browser</strong>
                <span>Keep changes in browser storage (temporary)</span>
              </div>
            </button>
            
            {hasUnsavedChanges && (
              <button 
                className="save-option clear-option"
                onClick={handleClearChanges}
              >
                <i className="fas fa-undo"></i>
                <div className="option-info">
                  <strong>Discard Changes</strong>
                  <span>Reload original data and lose all edits</span>
                </div>
              </button>
            )}
          </div>
          
          <div className="save-info">
            <p><strong>💡 Tip:</strong> Use the export controls above for advanced options including PDF reports and sharing. Download GEDCOM to permanently save changes.</p>
            {hasStoredChanges() && (
              <p><strong>📁 Status:</strong> You have unsaved changes stored in your browser.</p>
            )}
          </div>
        </div>
      )}
      
      {/* View Mode Toggle */}
      <div className="view-mode-controls">
        <div className="view-mode-toggle">
          <button
            className={`view-mode-btn ${viewMode === 'tree' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('tree')}
            title="Tree View"
          >
            <i className="fas fa-sitemap"></i>
            Tree
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('table')}
            title="Table View"
          >
            <i className="fas fa-table"></i>
            Table
          </button>
        </div>
      </div>
      
      {/* Controls - only show for tree view */}
      {viewMode === 'tree' && (
        <div className={`family-tree-controls ${isDesktopFullscreen ? 'family-tree-controls-sticky' : ''}`}>
          <div className="controls-row">
            <div className="control-group">
              <label>Root Person:</label>
              <select 
                value={selectedRoot} 
                onChange={(e) => handleRootChange(e.target.value)}
              >
                {rootPersons.map(person => (
                  <option key={person.id} value={person.id}>
                    {person.name} {person.birth && `(b. ${person.birth})`}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="control-group">
              <label>Search:</label>
              <div className="search-container">
                <div className="search-input-wrapper">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search names, dates, places..."
                  />
                  {searchTerm && (
                    <button 
                      className="clear-search"
                      onClick={() => {
                        setSearchTerm('');
                        setSearchResults([]);
                        updateUrlState({ search: '' });
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map(person => (
                      <div
                        key={person.id}
                        className="search-result"
                        onClick={() => handleSearchSelect(person.id)}
                      >
                        <i className="fas fa-user"></i>
                        <div className="search-result-info">
                          <div className="search-result-name">
                            {person.name}
                            {person.birth && <span className="search-result-year"> (b. {person.birth})</span>}
                          </div>
                          {person.matches && person.matches.length > 0 && (
                            <div className="search-result-matches">
                              Matches: {person.matches.map(match => {
                                switch(match) {
                                  case 'name': return 'Name';
                                  case 'birth': return 'Birth Date';
                                  case 'death': return 'Death Date';
                                  case 'birthPlace': return 'Birth Place';
                                  case 'deathPlace': return 'Death Place';
                                  default: return match;
                                }
                              }).join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* No results help */}
                {showNoResultsHelp && searchTerm.trim().length >= 2 && (
                  <div className="search-no-results">
                    <div className="no-results-message">
                      <i className="fas fa-search"></i>
                      <span>No results found for "{searchTerm}"</span>
                    </div>
                    {searchSuggestions.length > 0 && (
                      <div className="search-suggestions">
                        <div className="suggestions-label">Try searching for:</div>
                        <div className="suggestions-list">
                          {searchSuggestions.map(suggestion => (
                            <button
                              key={suggestion}
                              className="suggestion-btn"
                              onClick={() => handleSuggestionSelect(suggestion)}
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="control-group">
              <label>Filter by Gender:</label>
              <select 
                value={filters.gender} 
                onChange={(e) => setFilters({...filters, gender: e.target.value})}
              >
                <option value="all">All Genders</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>
          </div>

          <div className="controls-row">
            <div className="control-group">
              <label>Date Range:</label>
              <select 
                value={filters.dateRange} 
                onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
              >
                <option value="all">All Periods</option>
                <option value="1800-1850">1800-1850</option>
                <option value="1850-1900">1850-1900</option>
                <option value="1900-1950">1900-1950</option>
                <option value="1950-2000">1950-2000</option>
                <option value="2000+">2000+</option>
              </select>
            </div>

            <div className="control-group">
              <label>Location:</label>
              <select 
                value={filters.location} 
                onChange={(e) => setFilters({...filters, location: e.target.value})}
              >
                <option value="all">All Locations</option>
                {availableLocations.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <button 
                className="tool-btn clear-filters-btn"
                onClick={handleClearAllFilters}
                title="Clear all filters and search"
              >
                <i className="fas fa-times-circle"></i>
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div className={`family-tree-main-content ${isDesktopFullscreen ? 'family-tree-main-content-fullscreen' : ''}`}>
        {viewMode === 'tree' ? (
          <div className="family-tree-container" ref={treeContainer}>
            {treeData && (
              <Tree
                data={treeData}
                translate={translate}
                orientation="vertical"
                pathFunc="diagonal"
                nodeSize={{ x: 200, y: 150 }}
                separation={{ siblings: 1, nonSiblings: 2 }}
                renderCustomNodeElement={renderCustomNode}
                zoom={zoom}
                scaleExtent={{ min: 0.1, max: 3 }}
                enableLegacyTransitions={true}
                onZoom={(zoomData) => handleZoomChange(zoomData.zoom)}
              />
            )}
          </div>
        ) : (
          <TableView
            gedcomData={gedcomData}
            onPersonSelect={handleTablePersonSelect}
            onSwitchToTree={handleSwitchToTree}
            searchTerm={searchTerm}
            filters={filters}
          />
        )}
      </div>
      
      {/* Legend - only show for tree view */}
      {viewMode === 'tree' && (
        <div className="family-tree-legend">
          <div className="legend-item">
            <div className="legend-circle male"></div>
            <span>Male</span>
          </div>
          <div className="legend-item">
            <div className="legend-circle female"></div>
            <span>Female</span>
          </div>
          <div className="legend-item">
            <div className="legend-circle has-children"></div>
            <span>Has Children</span>
          </div>
          <p className="legend-note">
            <strong>Click:</strong> View details • <strong>Double-click:</strong> Expand/collapse
          </p>
        </div>
      )}

      {/* Enhanced Modals */}
      {showRelationshipFinder && (
        <RelationshipFinder
          gedcomData={gedcomData}
          onClose={() => setShowRelationshipFinder(false)}
          preselectedPerson={selectedPerson}
          onPersonSelect={handlePersonSelect}
        />
      )}
      </div>
    </div>
  );
};

export default FamilyTreePage;